import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import can from 'ember-can/helpers/can';
import type AbilitiesService from 'ember-can/services/abilities';
import { dropTask } from 'ember-concurrency';
import { and } from 'ember-truth-helpers';
import SlideToggle from 'garaje/components/slide-toggle';
import ConfirmationModal from 'garaje/components/ui/classic/confirmation-modal';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import type VisitorsConfiguration from 'garaje/models/visitors-configuration';
import type FlashMessagesService from 'garaje/services/flash-messages';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';

interface EmployeeSignOutDisabledSignature {
  Args: {
    visitorsConfiguration: VisitorsConfiguration;
  };
}

export default class EmployeeSignOutDisabled extends Component<EmployeeSignOutDisabledSignature> {
  @service declare flashMessages: FlashMessagesService;
  @service declare abilities: AbilitiesService;

  @tracked showEnableConfirmModal = false;
  @tracked showDisableConfirmModal = false;

  get toggleDisabled(): boolean {
    return this.saveTask.isRunning || this.abilities.cannot('manage-employee-sign-out-disabled visitors-configuration');
  }

  onToggle = (checked: boolean): void => {
    if (checked) {
      this.showEnableConfirmModal = true;
    } else {
      this.showDisableConfirmModal = true;
    }
  };

  closeEnableModal = (): void => {
    this.showEnableConfirmModal = false;
  };

  closeDisableModal = (): void => {
    this.showDisableConfirmModal = false;
  };

  confirmEnableTask = dropTask(async () => {
    await this.saveTask.perform(true);
    this.showEnableConfirmModal = false;
  });

  confirmDisableTask = dropTask(async () => {
    await this.saveTask.perform(false);
    this.showDisableConfirmModal = false;
  });

  saveTask = dropTask(async (value: boolean) => {
    const config = this.args.visitorsConfiguration;
    config.employeeSignOutDisabled = value;

    try {
      await config.save();
      this.flashMessages.showAndHideFlash('success', `Host visitor sign-out ${value ? 'disabled' : 'enabled'}.`);
    } catch (e) {
      config.rollbackAttributes();
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
    }
  });

  <template>
    <SettingsPanel data-test-employee-sign-out-disabled as |panel|>
      <panel.header
        @iconUri="/assets/images/lock-gray-circle.svg"
        @title="Disable host visitor sign-out"
        @description="Prevent hosts from signing out their own visitors. Only Admins can sign out visitors when enabled."
        as |header|
      >
        <header.controls>
          <SlideToggle
            data-test-employee-sign-out-disabled-toggle
            aria-disabled={{this.toggleDisabled}}
            @isChecked={{@visitorsConfiguration.employeeSignOutDisabled}}
            @isDisabled={{this.toggleDisabled}}
            @onClick={{this.onToggle}}
          />
        </header.controls>
      </panel.header>
    </SettingsPanel>

    {{#if (and this.showEnableConfirmModal (can "manage-employee-sign-out-disabled visitors-configuration"))}}
      <ConfirmationModal
        data-test-employee-sign-out-disabled-enable-modal
        @title="Disable host visitor sign-out?"
        @ctaText="Disable"
        @showTopRightCloseX={{true}}
        @onClose={{this.closeEnableModal}}
        @handleTaskAction={{this.confirmEnableTask.perform}}
        @hideCancelButton={{true}}
      >
        <p class="mt-4">
          Employees (hosts) will no longer be able to sign out visitors at this location.
        </p>

        <p class="mt-8">
          Front desk, location, and global admins can sign out visitors. If you need other roles to sign out visitors,
          you can configure that in
          <LinkTo @route="manage.roles">roles and permissions</LinkTo>.
        </p>
      </ConfirmationModal>
    {{/if}}

    {{#if (and this.showDisableConfirmModal (can "manage-employee-sign-out-disabled visitors-configuration"))}}
      <ConfirmationModal
        data-test-employee-sign-out-disabled-disable-modal
        @title="Enable host visitor sign-out?"
        @ctaText="Enable"
        @showTopRightCloseX={{true}}
        @onClose={{this.closeDisableModal}}
        @handleTaskAction={{this.confirmDisableTask.perform}}
        @hideCancelButton={{true}}
      >
        <p class="mt-4">
          All employees (hosts) will be able to sign out their visitors at this location again.
        </p>
      </ConfirmationModal>
    {{/if}}
  </template>
}
